import { CancelTokenSource } from 'axios';

import BaseAPI from 'utils/api/base';

import { ActivityResponse, APIRequest, SearchResponse } from 'utils/api/types';
import { UsersCreationParams } from 'utils/api/modules/user/user.types';

import { env } from 'config';

import {
  LicensesStatusResponse,
  OrganisationTimezone,
  SafetyMetricsResponse,
  SafetyMetricsMonth,
} from './organisations.types';

class OrganisationsAPI extends BaseAPI {
  loadMasterServiceAgreement = (): APIRequest<UsersCreationParams> => {
    const url = '/organisations/organisation-document/';
    const method = 'GET';

    return this.request<UsersCreationParams>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  loadOrganisationLogo = (cancelTokenSource?: CancelTokenSource): APIRequest<Blob> => {
    const url = '/organisations/logo/';
    const method = 'GET';

    return this.request<Blob>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
      responseType: 'blob',
      cancelTokenSource,
    });
  };

  uploadLogoFile = (file: File, cancelTokenSource: CancelTokenSource): APIRequest<ActivityResponse> => {
    const method = 'PUT';
    const url = `/organisations/logo/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<ActivityResponse>({
      url,
      method,
      data,
      cancelTokenSource,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  deleteLogoFile = (): APIRequest<ActivityResponse> => {
    const method = 'DELETE';
    const url = `/organisations/logo/`;

    return this.request<ActivityResponse>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  searchOrganisations = (
    params?: Partial<{ query: string; organisation: string; limit: number }>
  ): APIRequest<SearchResponse[]> => {
    const url = '/organisations/search/';
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  loadLicensesStatus = (): APIRequest<LicensesStatusResponse> => {
    const url = '/organisations/licenses/';
    const method = 'GET';

    return this.request<LicensesStatusResponse>({
      url,
      method,
    });
  };

  loadSafetyMetrics = (organisationId: string, year: string): APIRequest<SafetyMetricsResponse> => {
    const url = `/organisations/${organisationId}/employees_monthly_hours/${year}/`;
    const method = 'GET';

    return this.request<SafetyMetricsResponse>({
      url,
      method,
    });
  };

  getTimezone = (signal?: AbortSignal): APIRequest<OrganisationTimezone> => {
    const url = '/organisations/timezone/';
    const method = 'GET';

    return this.request<OrganisationTimezone>({ url, method, signal });
  };

  updateTimezone = (data: OrganisationTimezone): APIRequest<OrganisationTimezone> => {
    const url = '/organisations/timezone/';
    const method = 'PATCH';

    return this.request<OrganisationTimezone>({ url, method, data });
  };

  updateSafetyMetrics = (
    organisationId: string,
    year: string,
    data: SafetyMetricsMonth
  ): APIRequest<SafetyMetricsResponse> => {
    const url = `/organisations/${organisationId}/employees_monthly_hours/${year}/`;
    const method = 'PATCH';

    return this.request<SafetyMetricsResponse>({
      url,
      method,
      data,
    });
  };
}

export default OrganisationsAPI;
